import React from 'react';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { FireRatingTabProps } from './types';
import { DEFAULT_CONVERSION_CURRENCY } from '../submission-details/types';
import SubmissionCurrencyTabProps from '../submission-currency-tabs';
import FireModifiers from './components/fire-modifiers';
import FirePricingSummary from './components/fire-pricing-summary';
import FirePricingAnalysis from './components/fire-pricing-analysis';
import FormActionButtons from 'components/ui/form-action-buttons';
import useSocketIO, { ESocketEvents } from 'hooks/useWebSocket';
import { priceRatingGatewayUrl } from 'services/apis';
import { GetTailoredResultQueryDtoPriceRatingTypeEnum } from 'libs/pinnacle-sdk';
import { IPriceRatingUpdatedMessage } from 'services/apis/types';
import { useQueryClient } from 'react-query';
import { EQueryKeys } from 'constants/query-keys';

const FireRating: React.FC<FireRatingTabProps> = ({
  submission,
  includeBack,
  includeNext,
  navigateBack,
  navigateNext,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<string>(
    submission.originalCurrency || DEFAULT_CONVERSION_CURRENCY
  );

  const { sendMessage } = useSocketIO<IPriceRatingUpdatedMessage>({
    url: priceRatingGatewayUrl,
    requireAuth: true,
    onConnect: () => {
      sendMessage(ESocketEvents.SUBSCRIBE, {
        submissionId: submission.submissionId,
      });
    },
    registerEvents: [
      {
        event: ESocketEvents.PRICE_RATING_UPDATED,
        onMessage: async (message) => {
          if (
            message?.data?.type ===
              GetTailoredResultQueryDtoPriceRatingTypeEnum.FireRating &&
            message.submissionId === submission.submissionId
          ) {
            // Invalidate and refetch the relevant queries
            // from FireModifiers, FirePricingSummary, and FirePricingAnalysis
            await Promise.all([
              queryClient.invalidateQueries({
                queryKey: [EQueryKeys.FIRE_MODIFIERS, submission.submissionId],
              }),
              queryClient.invalidateQueries({
                queryKey: [
                  EQueryKeys.FIRE_PRICING_SUMMARY,
                  submission.submissionId,
                ],
              }),
              queryClient.invalidateQueries({
                queryKey: [
                  EQueryKeys.FIRE_PRICING_ANALYSIS,
                  submission.submissionId,
                ],
              }),
            ]);
          }
        },
      },
    ],
  });

  return (
    <div className="rounded-lg bg-[#E3F2FB]">
      <div className="flex items-center justify-between">
        <SubmissionCurrencyTabProps
          originalCurrency={submission.originalCurrency}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>
      <div className="flex flex-col gap-5 mb-4">
        <FireModifiers sendMessage={sendMessage} submission={submission} />
        <FirePricingSummary submission={submission} currency={activeTab} />
        <FirePricingAnalysis submission={submission} currency={activeTab} />
      </div>

      <FormActionButtons
        confirmText={t('general.next')}
        cancelText={t('general.back')}
        isConfirmDisabled={false}
        includeCancel={includeBack}
        includeConfirm={includeNext}
        confirmOutlined={false}
        onCancel={navigateBack}
        onConfirm={navigateNext}
      />
    </div>
  );
};

export default FireRating;
